import { useQuery } from "@tanstack/react-query"
import { Database, Download, FileArchive, HardDrive, Info, Users } from "lucide-react"
import { toast } from "sonner"

import { Panel, StatCard, StatGrid } from "@/components/superadmin/kit"
import { Button } from "@/components/ui/button"
import { ITACONDO_PROFILE_FILTER } from "@/lib/itacondoScope"
import { supabase } from "@/integrations/supabase/client"
import { cn } from "@/lib/utils"

const LIMITS = { database: 500, storage: 1000, users: 50000, records: 500000 }

function formatBytes(bytes: number) {
  if (!bytes) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`
}

function Meter({ label, value, detail, percent }: { label: string; value: string; detail: string; percent: number }) {
  const tone = percent >= 85 ? "bg-[hsl(358_82%_62%)]" : percent >= 65 ? "bg-[hsl(38_92%_62%)]" : "bg-[hsl(224_100%_64%)]"
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <span className="text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground"><span className="text-foreground">{value}</span> / {detail}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[hsl(228_8%_13%)]">
        <div className={cn("h-full rounded-full", tone)} style={{ width: `${Math.max(percent, 0.8)}%` }} />
      </div>
      <div className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground">{percent.toFixed(percent < 1 ? 2 : 0)}% usado</div>
    </div>
  )
}

const DataManagement = () => {
  const { data: db } = useQuery({
    queryKey: ["database-stats"],
    queryFn: async () => {
      const [users, condominiums, incidents, messages, packages, visitors] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }).or(ITACONDO_PROFILE_FILTER),
        supabase.from("condominiums").select("*", { count: "exact", head: true }),
        supabase.from("incidents").select("*", { count: "exact", head: true }),
        supabase.from("messages").select("*", { count: "exact", head: true }),
        supabase.from("packages").select("*", { count: "exact", head: true }),
        supabase.from("visitors").select("*", { count: "exact", head: true }),
      ])
      const all = [users, condominiums, incidents, messages, packages, visitors]
      return {
        users: users.count || 0,
        condominiums: condominiums.count || 0,
        incidents: incidents.count || 0,
        messages: messages.count || 0,
        packages: packages.count || 0,
        visitors: visitors.count || 0,
        totalRecords: all.reduce((t, r) => t + (r.count || 0), 0),
      }
    },
  })

  const { data: storage } = useQuery({
    queryKey: ["storage-stats"],
    queryFn: async () => {
      const { data: buckets = [] } = await supabase.storage.listBuckets()
      let totalSize = 0
      let totalFiles = 0
      for (const bucket of buckets) {
        const { data: files = [] } = await supabase.storage.from(bucket.name).list("", { limit: 1000 })
        totalFiles += files.length
        totalSize += files.reduce((t, f) => t + Number(f.metadata?.size || 0), 0)
      }
      return { totalSize, totalFiles, buckets: buckets.length }
    },
  })

  const pct = (v: number, max: number) => Math.min((v / max) * 100, 100)

  const exportSummary = () => {
    const blob = new Blob([JSON.stringify({ generatedAt: new Date().toISOString(), database: db, storage }, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `itacondo-resumo-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
    toast.success("Resumo exportado.")
  }

  const tables: [string, string, number][] = [
    ["Usuários", "profiles", db?.users || 0],
    ["Organizações", "condominiums", db?.condominiums || 0],
    ["Ocorrências", "incidents", db?.incidents || 0],
    ["Encomendas", "packages", db?.packages || 0],
    ["Visitantes", "visitors", db?.visitors || 0],
    ["Mensagens", "messages", db?.messages || 0],
  ]

  return (
    <>
      <StatGrid>
        <StatCard label="Registros" value={(db?.totalRecords ?? 0).toLocaleString("pt-BR")} hint="Nas tabelas principais" icon={Database} tone="accent" />
        <StatCard label="Usuários" value={(db?.users ?? 0).toLocaleString("pt-BR")} hint={`limite ${LIMITS.users.toLocaleString("pt-BR")}`} icon={Users} />
        <StatCard label="Arquivos" value={(storage?.totalFiles ?? 0).toLocaleString("pt-BR")} hint={`${storage?.buckets ?? 0} buckets`} icon={FileArchive} />
        <StatCard label="Storage usado" value={formatBytes(storage?.totalSize || 0)} hint={`de ${LIMITS.storage} MB`} icon={HardDrive} />
      </StatGrid>

      <div className="grid min-w-0 gap-3 lg:grid-cols-2">
        <Panel title="Capacidade" description="Uso frente aos limites do plano atual">
          <div className="space-y-5">
            <Meter label="Usuários" value={(db?.users ?? 0).toLocaleString("pt-BR")} detail={LIMITS.users.toLocaleString("pt-BR")} percent={pct(db?.users || 0, LIMITS.users)} />
            <Meter label="Registros" value={(db?.totalRecords ?? 0).toLocaleString("pt-BR")} detail={LIMITS.records.toLocaleString("pt-BR")} percent={pct(db?.totalRecords || 0, LIMITS.records)} />
            <Meter label="Storage de arquivos" value={formatBytes(storage?.totalSize || 0)} detail={`${LIMITS.storage} MB`} percent={pct(storage?.totalSize || 0, LIMITS.storage * 1024 * 1024)} />
          </div>
        </Panel>

        <Panel title="Registros por tabela" flush>
          <ul className="divide-y divide-border">
            {tables.map(([label, table, value]) => (
              <li key={table} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                <span className="flex-1 text-foreground">{label}</span>
                <code className="font-mono text-[11px] text-muted-foreground">{table}</code>
                <span className="w-16 text-right font-semibold tabular-nums text-foreground">{value.toLocaleString("pt-BR")}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[hsl(224_70%_16%)] text-[hsl(224_100%_80%)]"><Info className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-foreground">Backups do banco são feitos pelo Supabase</div>
            <div className="text-[12px] text-muted-foreground">Restauração e agendamento ficam no painel do Supabase. Aqui você exporta um resumo dos números acima.</div>
          </div>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportSummary}><Download className="h-4 w-4" />Exportar resumo</Button>
        </div>
      </Panel>
    </>
  )
}

export { DataManagement }
